import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useMessages } from "context/messages";
import { getToken } from "context/auth";

import Widget from "components/dashboard/widget";
import WidgetLoadingSkeleton from "components/dashboard/widgetLoadingSkeleton";
import ButtonText from "components/common/buttonText";
import InputSearch from "components/common/inputSearch";
import InputDateAndTime from "components/common/inputDateAndTime";
import Modal from "components/common/modal";

import {
  getDiscountCodesForParkingArea,
  getDiscountCodeUsages,
  createDiscountCode,
  updateDiscountCode,
  softDeleteDiscountCode,
  restoreDiscountCode,
} from "api/discountCodes";

const emptyForm = {
  code: "",
  campaignName: "",
  discountPercent: "",
  maxHoursPerBooking: "",
  maxTotalUsages: "",
  validFrom: "",
  validUntil: "",
};

const TABS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  DELETED: "deleted",
};

const DiscountCodesPage = () => {
  const { t } = useTranslation();
  const { showToast } = useMessages();
  const { organizationExtId, parkingAreaExtId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState(TABS.ACTIVE);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null); // null = create-läge; objekt = edit-läge
  const [form, setForm] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [selectedUsages, setSelectedUsages] = useState([]);
  const [openMenuFor, setOpenMenuFor] = useState(null);

  const loadCodes = async () => {
    try {
      setIsLoading(true);
      const token = await getToken();
      const data = await getDiscountCodesForParkingArea(token, parkingAreaExtId);
      setCodes(data || []);
    } catch (e) {
      showToast(t("generic.errorTitle"), t("discountCodes.loadFailed"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCodes();
  }, [parkingAreaExtId]);

  // C3: separera per status (Aktiva = IsActive och inte deleted, Inaktiva = inte deleted men IsActive=false, Raderade = deletedDateTimeUtc satt)
  const filteredByTab = useMemo(() => {
    const q = searchText.toLowerCase();
    return codes.filter((c) => {
      const matchesSearch =
        !q ||
        (c.code || "").toLowerCase().includes(q) ||
        (c.campaignName || "").toLowerCase().includes(q);
      if (!matchesSearch) return false;

      const isDeleted = !!c.deletedDateTimeUtc;
      if (activeTab === TABS.DELETED) return isDeleted;
      if (activeTab === TABS.INACTIVE) return !isDeleted && !c.isActive;
      return !isDeleted && c.isActive;
    });
  }, [codes, searchText, activeTab]);

  const counts = useMemo(() => {
    const active = codes.filter((c) => !c.deletedDateTimeUtc && c.isActive).length;
    const inactive = codes.filter((c) => !c.deletedDateTimeUtc && !c.isActive).length;
    const deleted = codes.filter((c) => !!c.deletedDateTimeUtc).length;
    return { active, inactive, deleted };
  }, [codes]);

  const openCreateModal = () => {
    setEditingCode(null);
    setForm(emptyForm);
    setShowFormModal(true);
  };

  // C7: edit-läge öppnar samma form med ifyllda värden + Code-fältet
  // är redigerbart.
  const openEditModal = (code) => {
    setOpenMenuFor(null);
    setEditingCode(code);
    setForm({
      code: code.code || "",
      campaignName: code.campaignName || "",
      discountPercent: code.discountPercent != null ? String(code.discountPercent) : "",
      maxHoursPerBooking: code.maxHoursPerBooking != null ? String(code.maxHoursPerBooking) : "",
      maxTotalUsages: code.maxTotalUsages != null ? String(code.maxTotalUsages) : "",
      validFrom: code.validFrom ? code.validFrom.substring(0, 16) : "",
      validUntil: code.validUntil ? code.validUntil.substring(0, 16) : "",
    });
    setShowFormModal(true);
  };

  // Validering — räknas från form-state, används både för per-fält-feedback och
  // för att blockera Save-knappen.
  const validateForm = (f) => {
    const errs = {};
    const code = (f.code || "").trim();
    if (!code) errs.code = t("discountCodes.errCodeRequired");
    else if (!/^[A-Z0-9_-]{1,32}$/.test(code)) errs.code = t("discountCodes.errCodeFormat");

    if ((f.campaignName || "").length > 100) errs.campaignName = t("discountCodes.errCampaignTooLong");

    if (f.discountPercent === "" || f.discountPercent == null) {
      errs.discountPercent = t("discountCodes.errPercentRequired");
    } else {
      const pct = parseInt(f.discountPercent, 10);
      if (isNaN(pct) || pct < 1 || pct > 100) errs.discountPercent = t("discountCodes.invalidPercent");
      else if (!/^\d+$/.test(String(f.discountPercent).trim())) errs.discountPercent = t("discountCodes.errPercentInteger");
    }

    if (f.maxTotalUsages !== "" && f.maxTotalUsages != null) {
      const n = parseInt(f.maxTotalUsages, 10);
      if (isNaN(n) || n < 0 || !/^\d+$/.test(String(f.maxTotalUsages).trim())) errs.maxTotalUsages = t("discountCodes.errUsagesInvalid");
    }

    if (f.maxHoursPerBooking !== "" && f.maxHoursPerBooking != null) {
      const n = parseInt(f.maxHoursPerBooking, 10);
      if (isNaN(n) || n < 0 || !/^\d+$/.test(String(f.maxHoursPerBooking).trim())) errs.maxHoursPerBooking = t("discountCodes.errHoursInvalid");
    }

    if (f.validFrom && f.validUntil) {
      const from = new Date(f.validFrom);
      const until = new Date(f.validUntil);
      if (!isNaN(from.getTime()) && !isNaN(until.getTime()) && until <= from) {
        errs.validUntil = t("discountCodes.errUntilBeforeFrom");
      }
    }
    return errs;
  };

  const errors = validateForm(form);
  const hasErrors = Object.keys(errors).length > 0;

  const handleSubmit = async () => {
    if (hasErrors) {
      // Visa första felet som toast — fält-feedback visas ändå inline
      const firstErr = Object.values(errors)[0];
      showToast(t("generic.errorTitle"), firstErr, "error");
      return;
    }
    const pct = parseInt(form.discountPercent, 10);
    try {
      setIsSubmitting(true);
      const token = await getToken();
      // 1.1: 0 eller tomt = obegränsat (skickas som null till backend)
      const parseUnlimited = (raw) => {
        if (raw === "" || raw == null) return null;
        const n = parseInt(raw, 10);
        return isNaN(n) || n <= 0 ? null : n;
      };
      if (editingCode) {
        await updateDiscountCode(token, editingCode.discountCodeExtId, {
          code: form.code.trim().toUpperCase(),
          campaignName: form.campaignName.trim() || null,
          discountPercent: pct,
          maxHoursPerBooking: parseUnlimited(form.maxHoursPerBooking),
          maxTotalUsages: parseUnlimited(form.maxTotalUsages),
          validFrom: form.validFrom || null,
          validUntil: form.validUntil || null,
          isActive: editingCode.isActive,
        });
        showToast(t("discountCodes.updated"), "", "success");
      } else {
        await createDiscountCode(token, {
          parkingAreaExtId,
          code: form.code.trim().toUpperCase(),
          campaignName: form.campaignName.trim() || null,
          discountPercent: pct,
          maxHoursPerBooking: parseUnlimited(form.maxHoursPerBooking),
          maxTotalUsages: parseUnlimited(form.maxTotalUsages),
          validFrom: form.validFrom || null,
          validUntil: form.validUntil || null,
        });
        showToast(t("discountCodes.created"), "", "success");
      }
      setShowFormModal(false);
      setEditingCode(null);
      setForm(emptyForm);
      await loadCodes();
    } catch (e) {
      showToast(t("generic.errorTitle"), editingCode ? t("discountCodes.updateFailed") : t("discountCodes.createFailed"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const setActive = async (code, isActive) => {
    setOpenMenuFor(null);
    try {
      const token = await getToken();
      await updateDiscountCode(token, code.discountCodeExtId, {
        code: code.code,
        campaignName: code.campaignName,
        discountPercent: code.discountPercent,
        maxHoursPerBooking: code.maxHoursPerBooking,
        maxTotalUsages: code.maxTotalUsages,
        validFrom: code.validFrom,
        validUntil: code.validUntil,
        isActive,
      });
      showToast(isActive ? t("discountCodes.reactivated") : t("discountCodes.deactivated"), "", "success");
      await loadCodes();
    } catch (e) {
      showToast(t("generic.errorTitle"), t("discountCodes.updateFailed"), "error");
    }
  };

  // C8: soft-delete
  const handleDelete = async (code) => {
    setOpenMenuFor(null);
    if (!window.confirm(t("discountCodes.deleteConfirm", { code: code.code }))) return;
    try {
      const token = await getToken();
      await softDeleteDiscountCode(token, code.discountCodeExtId);
      showToast(t("discountCodes.deleted"), "", "success");
      await loadCodes();
    } catch (e) {
      showToast(t("generic.errorTitle"), t("discountCodes.deleteFailed"), "error");
    }
  };

  // C8: återställ
  const handleRestore = async (code) => {
    setOpenMenuFor(null);
    try {
      const token = await getToken();
      await restoreDiscountCode(token, code.discountCodeExtId);
      showToast(t("discountCodes.restored"), "", "success");
      await loadCodes();
    } catch (e) {
      showToast(t("generic.errorTitle"), t("discountCodes.restoreFailed"), "error");
    }
  };

  const openDetails = async (code) => {
    setSelectedCode(code);
    try {
      const token = await getToken();
      const usages = await getDiscountCodeUsages(token, code.discountCodeExtId);
      setSelectedUsages(usages || []);
    } catch (e) {
      showToast(t("generic.errorTitle"), t("discountCodes.loadUsagesFailed"), "error");
    }
  };

  if (isLoading) return <WidgetLoadingSkeleton />;

  return (
    <Widget title={t("discountCodes.title")}>
      {/* C3: Tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #ddd", marginBottom: 12 }}>
        {[
          { key: TABS.ACTIVE, label: t("discountCodes.tabActive"), count: counts.active },
          { key: TABS.INACTIVE, label: t("discountCodes.tabInactive"), count: counts.inactive },
          { key: TABS.DELETED, label: t("discountCodes.tabDeleted"), count: counts.deleted },
        ].map((tab) => (
          <div
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "10px 16px",
              cursor: "pointer",
              borderBottom: activeTab === tab.key ? "2px solid #0a7" : "2px solid transparent",
              fontWeight: activeTab === tab.key ? 600 : 400,
            }}>
            {tab.label} ({tab.count})
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <InputSearch
          value={searchText}
          onChange={setSearchText}
          placeHolder={t("discountCodes.searchPlaceholder")}
        />
        {activeTab === TABS.ACTIVE && (
          <ButtonText onClick={openCreateModal}>
            {t("discountCodes.createButton")}
          </ButtonText>
        )}
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>{t("discountCodes.code")}</th>
            <th style={{ textAlign: "left" }}>{t("discountCodes.campaignName")}</th>
            <th style={{ textAlign: "left" }}>{t("discountCodes.percent")}</th>
            <th style={{ textAlign: "left" }}>{t("discountCodes.status")}</th>
            <th style={{ textAlign: "left" }}>{t("discountCodes.usages")}</th>
            <th style={{ textAlign: "left", width: 60 }}></th>
          </tr>
        </thead>
        <tbody>
          {filteredByTab.map((c) => (
            <tr key={c.discountCodeExtId} style={{ cursor: "pointer" }}>
              <td onClick={() => openDetails(c)}>{c.code}</td>
              <td onClick={() => openDetails(c)}>{c.campaignName || "-"}</td>
              <td onClick={() => openDetails(c)}>{c.discountPercent}%</td>
              <td onClick={() => openDetails(c)}>{t(`discountCodes.status_${c.status}`)}</td>
              <td onClick={() => openDetails(c)}>
                {c.currentUsageCount}
                {c.maxTotalUsages ? ` / ${c.maxTotalUsages}` : ""}
              </td>
              {/* C4: kontextmeny via tre prickar */}
              <td onClick={(e) => e.stopPropagation()} style={{ position: "relative" }}>
                <button
                  onClick={() => setOpenMenuFor(openMenuFor === c.discountCodeExtId ? null : c.discountCodeExtId)}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 8px", fontSize: 18 }}>
                  ⋯
                </button>
                {openMenuFor === c.discountCodeExtId && (
                  <div style={{
                    position: "absolute", right: 0, top: 30, background: "white",
                    border: "1px solid #ccc", borderRadius: 6, boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    zIndex: 10, minWidth: 140
                  }}>
                    {!c.deletedDateTimeUtc && (
                      <div onClick={() => openEditModal(c)} style={{ padding: 8, cursor: "pointer" }}>
                        {t("discountCodes.edit")}
                      </div>
                    )}
                    {!c.deletedDateTimeUtc && c.isActive && (
                      <div onClick={() => setActive(c, false)} style={{ padding: 8, cursor: "pointer" }}>
                        {t("discountCodes.deactivate")}
                      </div>
                    )}
                    {!c.deletedDateTimeUtc && !c.isActive && (
                      <div onClick={() => setActive(c, true)} style={{ padding: 8, cursor: "pointer" }}>
                        {t("discountCodes.reactivate")}
                      </div>
                    )}
                    {!c.deletedDateTimeUtc && (
                      <div onClick={() => handleDelete(c)} style={{ padding: 8, cursor: "pointer", color: "#c00" }}>
                        {t("discountCodes.delete")}
                      </div>
                    )}
                    {c.deletedDateTimeUtc && (
                      <div onClick={() => handleRestore(c)} style={{ padding: 8, cursor: "pointer", color: "#0a7" }}>
                        {t("discountCodes.restore")}
                      </div>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showFormModal && (
        <Modal
          display={showFormModal}
          setDisplay={(v) => {
            if (!v) { setShowFormModal(false); setEditingCode(null); }
          }}
          onClose={() => { setShowFormModal(false); setEditingCode(null); }}
          title={editingCode ? t("discountCodes.editTitle") : t("discountCodes.createTitle")}
          innerClassName="!w-[560px] !max-w-[95vw] bg-white rounded-lg p-6"
        >
          {(() => {
            const baseInput = "w-full h-11 bg-airpark-gray-300 rounded-xl px-4 text-airpark-input-field focus:outline-none";
            const labelCls = "text-sm font-medium text-gray-700 mb-1";
            const errCls = "border-2 border-airpark-strawberry-500";
            const fieldCls = (key, extra = "") => `${baseInput}${errors[key] ? " " + errCls : ""}${extra ? " " + extra : ""}`;
            const errMsg = (key) => errors[key] ? <span className="text-xs text-airpark-strawberry-500 mt-1">{errors[key]}</span> : null;
            // För Kod: tillåt bara A-Z 0-9 _ -, max 32 — sanitera direkt vid input
            const sanitizeCode = (raw) => (raw || "").toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 32);
            // För numeriska fält: tillåt bara siffror
            const sanitizeInt = (raw) => (raw || "").replace(/[^0-9]/g, "");
            return (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col">
                  <label className={labelCls}>{t("discountCodes.code")} *</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: sanitizeCode(e.target.value) })}
                    className={fieldCls("code", "uppercase tracking-wider")}
                    autoCapitalize="characters"
                    maxLength={32}
                  />
                  {errMsg("code")}
                </div>

                <div className="flex flex-col">
                  <label className={labelCls}>{t("discountCodes.campaignName")}</label>
                  <input
                    type="text"
                    value={form.campaignName}
                    onChange={(e) => setForm({ ...form, campaignName: e.target.value })}
                    className={fieldCls("campaignName")}
                    maxLength={100}
                  />
                  {errMsg("campaignName")}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className={labelCls}>{t("discountCodes.percent")} *</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.discountPercent}
                      onChange={(e) => {
                        const cleaned = sanitizeInt(e.target.value);
                        // Klippa till 100 om användaren skriver 1111
                        const clamped = cleaned === "" ? "" : Math.min(parseInt(cleaned, 10) || 0, 100).toString();
                        setForm({ ...form, discountPercent: clamped });
                      }}
                      className={fieldCls("discountPercent")}
                      placeholder="1–100"
                      maxLength={3}
                    />
                    {errMsg("discountPercent")}
                  </div>
                  <div className="flex flex-col">
                    <label className={labelCls}>{t("discountCodes.maxUsages")}</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.maxTotalUsages}
                      onChange={(e) => setForm({ ...form, maxTotalUsages: sanitizeInt(e.target.value) })}
                      className={fieldCls("maxTotalUsages")}
                      placeholder={t("discountCodes.unlimitedHint")}
                      maxLength={9}
                    />
                    {errMsg("maxTotalUsages")}
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className={labelCls}>{t("discountCodes.maxHoursLabel")}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={form.maxHoursPerBooking}
                    onChange={(e) => setForm({ ...form, maxHoursPerBooking: sanitizeInt(e.target.value) })}
                    className={fieldCls("maxHoursPerBooking")}
                    placeholder={t("discountCodes.unlimitedHint")}
                    maxLength={5}
                  />
                  {errMsg("maxHoursPerBooking")}
                </div>

                <div className="flex flex-col gap-4">
                  <div className="flex flex-col">
                    <label className={labelCls}>{t("discountCodes.validFrom")}</label>
                    <InputDateAndTime
                      value={form.validFrom || new Date()}
                      onChange={(d) => setForm({ ...form, validFrom: d.toISOString() })}
                    />
                    {errMsg("validFrom")}
                  </div>
                  <div className="flex flex-col">
                    <label className={labelCls}>{t("discountCodes.validUntil")}</label>
                    <InputDateAndTime
                      value={form.validUntil || new Date()}
                      onChange={(d) => setForm({ ...form, validUntil: d.toISOString() })}
                    />
                    {errMsg("validUntil")}
                  </div>
                </div>

                <div className="flex gap-3 justify-end mt-2">
                  <ButtonText
                    isSecondary
                    onClick={() => { setShowFormModal(false); setEditingCode(null); }}
                    disabled={isSubmitting}
                  >
                    {t("generic.cancel")}
                  </ButtonText>
                  <ButtonText onClick={handleSubmit} disabled={isSubmitting || hasErrors}>
                    {t("discountCodes.save")}
                  </ButtonText>
                </div>
              </div>
            );
          })()}
        </Modal>
      )}

      {selectedCode && (() => {
        // Senaste 6 månaderna med data — gruppera selectedUsages per år-månad.
        const monthlyStats = (() => {
          const buckets = new Map();
          for (const u of selectedUsages) {
            const d = new Date(u.createdDateTimeUtc);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            const cur = buckets.get(key) || { key, year: d.getFullYear(), month: d.getMonth(), count: 0, totalDiscount: 0 };
            cur.count += 1;
            cur.totalDiscount += Number(u.discountedAmount) || 0;
            buckets.set(key, cur);
          }
          return Array.from(buckets.values())
            .sort((a, b) => b.key.localeCompare(a.key))
            .slice(0, 6);
        })();

        const monthLabel = (year, monthIdx) =>
          new Date(year, monthIdx, 1).toLocaleString(undefined, { month: "long", year: "numeric" });

        const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";
        const isDeleted = !!selectedCode.deletedDateTimeUtc;
        const isActive = selectedCode.isActive;

        // Stäng + utför handler — vanligt mönster för flera knappar
        const closeAnd = (fn) => () => { setSelectedCode(null); fn(); };

        return (
          <Modal
            display={!!selectedCode}
            setDisplay={(v) => { if (!v) setSelectedCode(null); }}
            onClose={() => setSelectedCode(null)}
            innerClassName="!w-[640px] !max-w-[95vw] bg-white rounded-lg p-6"
          >
            <div className="flex flex-col gap-4">
              {/* Header med kampanjnamn + status */}
              <div className="flex justify-between items-start border-b border-airpark-gray-300 pb-3">
                <div>
                  <div className="text-2xl font-semibold">{selectedCode.code}</div>
                  {selectedCode.campaignName && (
                    <div className="text-sm text-gray-600 mt-1">{selectedCode.campaignName}</div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 uppercase">{t("discountCodes.status")}</div>
                  <div className="font-medium">{t(`discountCodes.status_${selectedCode.status}`)}</div>
                </div>
              </div>

              {/* Aggregerade nyckeltal */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-airpark-gray-300 rounded-xl p-4">
                  <div className="text-xs text-gray-600 uppercase">{t("discountCodes.usages")}</div>
                  <div className="text-2xl font-semibold mt-1">
                    {selectedCode.currentUsageCount}
                    {selectedCode.maxTotalUsages ? (
                      <span className="text-sm text-gray-500"> / {selectedCode.maxTotalUsages}</span>
                    ) : null}
                  </div>
                </div>
                <div className="bg-airpark-gray-300 rounded-xl p-4">
                  <div className="text-xs text-gray-600 uppercase">{t("discountCodes.totalDiscounted")}</div>
                  <div className="text-2xl font-semibold mt-1">
                    {Number(selectedCode.totalDiscountedAmount || 0).toFixed(2)} kr
                  </div>
                </div>
              </div>

              {/* Meta-info: rabatt %, giltighet, skapad */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-gray-600">{t("discountCodes.percent")}</div>
                  <div className="font-medium">{selectedCode.discountPercent}%</div>
                </div>
                <div>
                  <div className="text-gray-600">{t("discountCodes.validFrom")} / {t("discountCodes.validUntil")}</div>
                  <div className="font-medium">
                    {selectedCode.validFrom ? formatDate(selectedCode.validFrom) : "—"} – {selectedCode.validUntil ? formatDate(selectedCode.validUntil) : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-gray-600">{t("discountCodes.createdAt")}</div>
                  <div className="font-medium">{formatDate(selectedCode.createdDateTimeUtc)}</div>
                </div>
              </div>

              {isDeleted && (
                <div className="text-sm text-airpark-strawberry-500">
                  <strong>{t("discountCodes.deletedAt")}:</strong> {new Date(selectedCode.deletedDateTimeUtc).toLocaleString()}
                </div>
              )}

              {/* Månadsvis aggregering — senaste 6 månaderna med data */}
              <div>
                <h4 className="text-sm font-semibold mb-2">{t("discountCodes.monthlyBreakdown")}</h4>
                {monthlyStats.length === 0 ? (
                  <div className="text-sm text-gray-500 italic">{t("discountCodes.noUsagesYet")}</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-600 text-xs uppercase border-b border-airpark-gray-300">
                        <th className="text-left py-2 font-medium">{t("discountCodes.month")}</th>
                        <th className="text-right py-2 font-medium">{t("discountCodes.usages")}</th>
                        <th className="text-right py-2 font-medium">{t("discountCodes.totalDiscounted")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyStats.map((m) => (
                        <tr key={m.key} className="border-b border-airpark-gray-300 last:border-0">
                          <td className="py-2 capitalize">{monthLabel(m.year, m.month)}</td>
                          <td className="py-2 text-right">{m.count}</td>
                          <td className="py-2 text-right">{m.totalDiscount.toFixed(2)} kr</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Länk till bokningar */}
              <button
                type="button"
                onClick={() => {
                  const code = selectedCode.code;
                  setSelectedCode(null);
                  navigate(`/${organizationExtId}/parking-areas/${parkingAreaExtId}/bookings?discountCode=${encodeURIComponent(code)}`);
                }}
                className="text-airpark-green underline text-sm text-left self-start"
              >
                {t("discountCodes.viewBookings")}
              </button>

              {/* Status-beroende actions — speglar tre-prickar-menyn */}
              <div className="flex gap-3 justify-end border-t border-airpark-gray-300 pt-4">
                {!isDeleted && (
                  <ButtonText isSecondary onClick={closeAnd(() => openEditModal(selectedCode))}>
                    {t("discountCodes.edit")}
                  </ButtonText>
                )}
                {!isDeleted && isActive && (
                  <ButtonText isSecondary onClick={closeAnd(() => setActive(selectedCode, false))}>
                    {t("discountCodes.deactivate")}
                  </ButtonText>
                )}
                {!isDeleted && !isActive && (
                  <ButtonText isSecondary onClick={closeAnd(() => setActive(selectedCode, true))}>
                    {t("discountCodes.reactivate")}
                  </ButtonText>
                )}
                {!isDeleted && (
                  <ButtonText isDelete onClick={closeAnd(() => handleDelete(selectedCode))}>
                    {t("discountCodes.delete")}
                  </ButtonText>
                )}
                {isDeleted && (
                  <ButtonText onClick={closeAnd(() => handleRestore(selectedCode))}>
                    {t("discountCodes.restore")}
                  </ButtonText>
                )}
              </div>
            </div>
          </Modal>
        );
      })()}
    </Widget>
  );
};

export default DiscountCodesPage;
